'use client'

import { ChevronLeft } from 'lucide-react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useRegion } from '@/shared/hooks/use-region'
import { buildRegionUrl } from '@/shared/lib/utils'
import {
  SEARCH_PARAMS_RETURN_GROUP_ID_KEY,
  SEARCH_PARAMS_TAB_KEY,
  SEARCH_PARAMS_WEEK_START_KEY,
} from '@/features/volunteers/constants/volunteer-tab-links'

const FROM_CALENDAR = 'calendar'
const FROM_GROUPS = 'groups'
const FROM_DASHBOARD = 'dashboard'

export function GroupBackButton() {
  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { regionSlug } = useRegion()
  const from = searchParams.get('from')
  const tab = searchParams.get('tab')
  const weekStart = searchParams.get(SEARCH_PARAMS_WEEK_START_KEY)
  const returnToGroupId = searchParams.get(SEARCH_PARAMS_RETURN_GROUP_ID_KEY)

  const handleBack = () => {
    if (tab === 'members' || tab === 'pretrip' || tab === 'photos') {
      const params = new URLSearchParams()
      params.set('tab', 'dashboard')
      if (from) params.set('from', from)
      if (weekStart) params.set(SEARCH_PARAMS_WEEK_START_KEY, weekStart)
      if (returnToGroupId) params.set(SEARCH_PARAMS_RETURN_GROUP_ID_KEY, returnToGroupId)
      const href = `${pathname}?${params.toString()}`
      router.replace(href)
      return
    }
    if (tab === 'dashboard' && !from) {
      const href = regionSlug
        ? buildRegionUrl(regionSlug, '/volunteers', { [SEARCH_PARAMS_TAB_KEY]: 'groups' })
        : `/volunteers?${SEARCH_PARAMS_TAB_KEY}=groups`
      router.push(href)
      return
    }
    // Use ?from= when we have it so we return to the right Volunteers tab
    if (from === FROM_CALENDAR) {
      const calendarParams: Record<string, string> = { [SEARCH_PARAMS_TAB_KEY]: 'calendar' }
      if (weekStart) calendarParams[SEARCH_PARAMS_WEEK_START_KEY] = weekStart
      const href = regionSlug
        ? buildRegionUrl(regionSlug, '/volunteers', calendarParams)
        : `/volunteers?${new URLSearchParams(calendarParams).toString()}`
      router.push(href)
      return
    }
    if (from === FROM_GROUPS) {
      const groupsParams: Record<string, string> = { [SEARCH_PARAMS_TAB_KEY]: 'groups' }
      if (weekStart) groupsParams[SEARCH_PARAMS_WEEK_START_KEY] = weekStart
      if (returnToGroupId) groupsParams[SEARCH_PARAMS_RETURN_GROUP_ID_KEY] = returnToGroupId
      const href = regionSlug
        ? buildRegionUrl(regionSlug, '/volunteers', groupsParams)
        : `/volunteers?${new URLSearchParams(groupsParams).toString()}`
      router.push(href)
      return
    }
    if (from === FROM_DASHBOARD) {
      // Prefer browser back so we return to the same dashboard state/scroll position.
      if (typeof window !== 'undefined' && window.history.length > 1) {
        router.back()
        return
      }
      const href = regionSlug
        ? buildRegionUrl(regionSlug, '/volunteers', { [SEARCH_PARAMS_TAB_KEY]: 'dashboard' })
        : `/volunteers?${SEARCH_PARAMS_TAB_KEY}=dashboard`
      router.push(href)
      return
    }
    // No from param: try browser back, else fallback to Groups
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back()
    } else {
      const href = regionSlug
        ? buildRegionUrl(regionSlug, '/volunteers', { [SEARCH_PARAMS_TAB_KEY]: 'groups' })
        : `/volunteers?${SEARCH_PARAMS_TAB_KEY}=groups`
      router.push(href)
    }
  }

  return (
    <button type="button" onClick={handleBack} aria-label="Back">
      <ChevronLeft className="w-7 h-7" />
    </button>
  )
}
